import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VmeCommonModule } from '../../vme-common.module';
import { DialogProgressComponent } from './dialog-progress/dialog-progress.component';
import { TravelSchedulerSummaryComponent } from './travel-scheduler-summary/travel-scheduler-summary.component';
import {
  TravelSchedulerDTO,
  TravelSchedulerTask,
} from '../../interfaces/travel-scheduler/travel-scheduler-task';
import { ScheduleTaskComponent } from './schedule-task/schedule-task.component';
import { ScheduleTaskSequenceComponent } from './schedule-task-sequence/schedule-task-sequence.component';
import { TravelSchedulerService } from '../../services/travel-scheduler.service';
import { UpdateSupplierScheduleRequest } from '../../interfaces/travel-scheduler/update-supplier-schedule-request';
import { MessageService } from 'primeng';
import { Message } from 'src/app/classes/Message';
import { RefinerSurveyService } from '../../services/refiner-survey.service';
import { ValidateCarryoverAddressResponse } from '../../interfaces/carryover/validate-carryover-address';
import { CarryoverValidationUtils } from './carryover-validation-utils';
import { OrganisationsService } from '../../services/organisations.service';
import { WarningContainerComponent } from '../create-movement-job/warning-container/warning-container.component';
import { OrganisationTypeEnum } from '../../enums/organisation-types';
import { isDateToday } from '../../utils/date-utils';

@Component({
  selector: 'travel-scheduler',
  standalone: true,
  imports: [
    CommonModule,
    VmeCommonModule,
    DialogProgressComponent,
    TravelSchedulerSummaryComponent,
    ScheduleTaskComponent,
    ScheduleTaskSequenceComponent,
    WarningContainerComponent,
  ],
  templateUrl: './travel-scheduler.component.html',
  styleUrls: ['./travel-scheduler.component.scss'],
})
export class TravelSchedulerComponent implements OnInit {
  constructor(
    private travelSchedulerService: TravelSchedulerService,
    private messageService: MessageService,
    private refinerService: RefinerSurveyService,
    private organisationsService: OrganisationsService,
  ) {}

  protected data: TravelSchedulerDTO;

  @Input() id: number;

  @Input() isAfterCutoff: boolean = false;

  protected visible: boolean = true;
  protected organisationTypeId: number | null = null;
  protected isTaskAlreadyScheduledForTodayLookup: { [taskId: number]: boolean } = {};

  async ngOnInit() {
    this.isLoading = true;

    try {
      this.organisationTypeId = await this.organisationsService.getCurrentUserOrgTypeId();
      this.data = await this.travelSchedulerService.get(this.id);

      this.tasks = this.data.tasks;
      this.tasks.forEach((task) => {
        this.isTaskAlreadyScheduledForTodayLookup[task.id] =
          isDateToday(task.supplierPickupWindow) || isDateToday(task.supplierDropoffWindow);
      });
      this.taskIndex = this.data.isFlexible ? -1 : 0;
      this.isLoading = false;
    } catch {
      console.error('Error loading schedule');
      this.messageService.add(Message.Error('Error loading schedule'));
    }
  }

  protected isLoading: boolean = true;

  protected taskIndex: number = -1;

  protected tasks: TravelSchedulerTask[];

  protected close() {
    this.visible = false;
    this.onCancel.emit();
  }

  @Output() onCancel = new EventEmitter<void>();
  @Output() onCompleteSchedule = new EventEmitter<void>();

  protected handleScheduleSelected(task: TravelSchedulerTask) {
    this.tasks[this.taskIndex] = task;
  }

  protected getCarryoverValidationResult(): ValidateCarryoverAddressResponse {
    return this.getCurrentTask()?.carryoverValidation ?? null;
  }

  protected handleCarryoverValidationResult(result: ValidateCarryoverAddressResponse) {
    const task = this.getCurrentTask();

    if (!task) {
      return;
    }

    task.carryoverAddressChanged = true;
    task.carryoverValidation = result;
  }

  private getCurrentTask(): TravelSchedulerTask {
    if (this.taskIndex < 0 || !this.tasks) {
      return null;
    }

    return this.tasks[this.taskIndex];
  }

  /** Explains why Continue is unavailable. Shown on the button itself, so it also works while disabled. */
  protected get continueBtnTooltip(): string {
    const task = this.getCurrentTask();

    if (task && !CarryoverValidationUtils.isCarryoverValidated(task)) {
      return 'A suitable carryover parking address is needed before continuing.';
    }

    return null;
  }

  protected continue() {
    const task = this.getCurrentTask();

    if (!CarryoverValidationUtils.isCarryoverValidated(task)) {
      this.messageService.add(
        Message.Error('A suitable carryover parking address is needed before continuing.'),
      );
      return;
    }

    this.taskIndex++;
  }

  get backButtonDisabled() {
    return this.taskIndex === -1 || (this.taskIndex === 0 && !this.data?.isFlexible);
  }

  protected back() {
    if (this.taskIndex > -1) {
      this.taskIndex--;
    }
  }

  // Used to stop spam clicks on complete button.
  protected completeDisabled: boolean = false;

  protected async complete() {
    this.completeDisabled = true;

    for (const task of this.tasks) {
      if (!task.isSupplierCarryingover) {
        task.carryoverAddress = null;
      }
    }

    const request: UpdateSupplierScheduleRequest = {
      jobId: this.id,
      taskSupplierSchedules: this.tasks.map((task) => ({
        taskId: task.id,
        specifiedPickUpTime: task.supplierPickupWindow,
        specifiedDropOffTime: task.supplierDropoffWindow,
        transportMode: task.supplierTransportModeToPickup,
        taskCollectionTravelMode: task.supplierTransportModeToPickup,
        carryoverAddress: task.carryoverAddress,
      })),
    };

    try {
      await this.travelSchedulerService.updateSupplierSchedule(request);
      this.messageService.add(Message.Success('Schedule Saved'));
      this.onCompleteSchedule.emit();
    } catch (error) {
      console.error(error);
      this.messageService.add(
        Message.Error('Error Saving Schedule. Refresh the page and try again.'),
      );
    } finally {
      this.refinerService.trackEvent('TravelSchedulerCompleted');
      this.completeDisabled = false;
    }
  }

  editSchedule($event: number) {
    this.taskIndex = $event;
  }

  getNumberOfSteps() {
    let steps = this.tasks.length + 1;

    if (this.data.isFlexible) {
      steps++;
    }

    return steps;
  }

  getCurrentStep() {
    let step = this.taskIndex;

    if (this.data.isFlexible) {
      step++;
    }

    return step;
  }

  isContinueButtonDisabled() {
    if (this.taskIndex == -1) {
      return false;
    }

    if (
      this.tasks[this.taskIndex].isSupplierCarryingover &&
      this.tasks[this.taskIndex].carryoverAddress == null
    ) {
      return true;
    }

    if (!CarryoverValidationUtils.isCarryoverValidated(this.tasks[this.taskIndex])) {
      return true;
    }

    if (
      this.tasks[this.taskIndex].supplierDropoffWindow != null &&
      this.tasks[this.taskIndex].supplierPickupWindow != null
    ) {
      return false;
    }
    return true;
  }

  getPreviousTaskDropoffWindow() {
    if (this.taskIndex <= 0) {
      return null;
    }

    const supplierDropoffWindow = this.tasks[this.taskIndex - 1].supplierDropoffWindow;

    return typeof supplierDropoffWindow == 'string'
      ? new Date(supplierDropoffWindow)
      : supplierDropoffWindow;
  }

  protected readonly OrganisationTypeEnum = OrganisationTypeEnum;
}
